let bffEnabled = false;
let sessionOk: boolean | null = null;
let sessionProbe$: Promise<SessionProbeVerdict> | null = null;
// Владелец куки по последней пробе; сброс кэша (invalidateBffSession) его не стирает — стирает только ответ «сессии нет».
let bffUserId: string | null = null;
let bffImpersonation = false;

export type SessionProbeVerdict = 'alive' | 'dead' | 'unknown';

export function isBffMode(): boolean {
  return bffEnabled;
}

export function readBffUserId(): string | null {
  return bffUserId;
}

/** Staff works under the user's login (`impersonation` of the last live probe). */
export function readBffImpersonation(): boolean {
  return bffImpersonation;
}

export async function initAuthMode(): Promise<void> {
  try {
    const res = await fetch('/api/auth/bff/config');
    bffEnabled = res.ok && (await res.json()).enabled === true;
  } catch {
    bffEnabled = false;
  }
}

export async function hasBffSession(): Promise<boolean> {
  if (sessionOk === true) return true;
  // routed through the same single-flight probe as probeBffSession() — no independent writer can race sessionOk
  const verdict = await probeBffSession();
  return verdict === 'alive';
}

export function invalidateBffSession(): void {
  sessionOk = null;
}

// Bypasses the hasBffSession() cache so a post-401 check reflects the server's current verdict,
// not a stale positive read from before the session died; concurrent callers share one request.
export function probeBffSession(): Promise<SessionProbeVerdict> {
  if (!sessionProbe$) {
    sessionProbe$ = runSessionProbe().finally(() => {
      sessionProbe$ = null;
    });
  }

  return sessionProbe$;
}

async function runSessionProbe(): Promise<SessionProbeVerdict> {
  try {
    const res = await fetch('/api/auth/bff/session');
    if (!res.ok) return 'unknown';
    const body = await res.json();
    if (body?.authenticated === true) {
      sessionOk = true;
      bffUserId = typeof body.userId === 'string' ? body.userId : null;
      bffImpersonation = body.impersonation === true;
      return 'alive';
    }

    sessionOk = null;
    bffUserId = null;
    bffImpersonation = false;
    return 'dead';
  } catch {
    return 'unknown';
  }
}

export function buildBffLoginUrl(returnUrl?: string): string {
  return returnUrl ? `/api/auth/bff/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/api/auth/bff/login';
}

export function redirectToBffLogin(returnUrl?: string): void {
  window.location.assign(buildBffLoginUrl(returnUrl));
}

// Тёплый выход — до трёх запросов к Hydra по 10 с (30 с); холодный с discovery core и media — до 50 с, покрыт не целиком.
export const BFF_LOGOUT_TIMEOUT_MS = 45_000;

export async function bffLogout(): Promise<void> {
  try {
    await fetch('/api/auth/bff/logout', { method: 'POST', signal: AbortSignal.timeout(BFF_LOGOUT_TIMEOUT_MS) });
  } catch {
    // сервер недоступен или не ответил за BFF_LOGOUT_TIMEOUT_MS — сессия всё равно умрёт на следующем guard-чеке
  }

  invalidateBffSession();
}

export function resetBffStateForTests(): void {
  bffEnabled = false;
  sessionOk = null;
  sessionProbe$ = null;
  bffUserId = null;
  bffImpersonation = false;
}
